import {
  sessionsCsvUrl,
  SESSIONS_PAST_GID,
  SESSIONS_UPCOMING_GID,
  PYCRAY_FORGE_CHANNEL_ID,
  PYCRAY_FORGE_VIDEO_FALLBACK,
} from "../constants/sessions";

export const SESSIONS_FALLBACK = {
  upcoming: {
    title: "How to Give a Great Tech Talk at Work",
    date: "Sunday, 20 September 2026",
    time: "12:00 – 1:00 PM IST",
    speaker: "Rahul Das · Co-Founder, PyCray Technologies (IIM Udaipur)",
    description:
      "Rahul drives go-to-market strategy for AI, data, and cloud solutions — bridging technical architecture with commercial solutioning to help clients evaluate, adopt, and scale intelligent platforms confidently. This session will break down how to present your work effectively to both technical and non-technical stakeholders — a skill that directly impacts how you are perceived and how fast you grow in any tech role.",
    registrationLink: "https://forms.cloud.microsoft/r/bmi39PBEAL",
  },
  past: [
    {
      number: 8,
      title: "How to Read and Write Technical Documentation Like a Pro",
      learnings: [
        "PRDs",
        "SRS",
        "BRDs",
        "API docs",
        "How to read and write them at work",
      ],
      recordingLink: "https://www.youtube.com/@PyCrayForge",
    },
    {
      number: 7,
      title: "How Software Actually Gets Released — From Code to Customer",
      learnings: [
        "Staging",
        "Deployments",
        "Go-live",
        "What every role does when software ships",
      ],
      recordingLink: "https://youtu.be/JEYMlEF88wA",
    },
    {
      number: 6,
      title: "What Is Product Thinking — and Why Every Tech Role Needs It",
      learnings: [
        "Product mindset",
        "Why every role needs it",
        "How real teams make decisions",
      ],
      recordingLink: "https://www.youtube.com/@PyCrayForge",
    },
    {
      number: 5,
      title: "How to Use AI Tools to Get Ahead in Your Tech Career",
      learnings: [
        "ChatGPT",
        "GitHub Copilot",
        "Claude",
        "AI tools by role",
        "What AI-native means on a JD",
      ],
      recordingLink: "https://youtu.be/JEYMlEF88wA",
    },
    {
      number: 4,
      title: "How to Crack a Tech Interview With Zero Work Experience",
      learnings: [
        "Talking about college projects",
        "What interviewers look for",
        "Handling technical and behavioural questions",
      ],
      recordingLink: "https://youtu.be/UCE6O9fZPrQ",
    },
    {
      number: 3,
      title: "Build A Portfolio That Gets You Hired",
      learnings: [
        "What hiring managers look for",
        "How to make projects stand out",
        "GitHub and portfolio tips",
      ],
      recordingLink: "https://www.youtube.com/@PyCrayForge",
    },
    {
      number: 2,
      title: "Which Tech Role Is Actually For You?",
      learnings: [
        "FE vs BE vs QA vs PM vs BSA",
        "Day-to-day reality",
        "Career trajectory",
        "How to self-select",
      ],
      recordingLink: "https://www.youtube.com/@PyCrayForge",
    },
    {
      number: 1,
      title: "Your First 30 Days at a Tech Job",
      learnings: [
        "Onboarding",
        "Standups",
        "Sprint cycles",
        "What managers actually expect in month one",
      ],
      recordingLink: "https://www.youtube.com/@PyCrayForge",
    },
  ],
};

function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (char === '"') {
        if (next === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n" || (char === "\r" && next === "\n")) {
      if (char === "\r") i += 1;
      row.push(cell);
      cell = "";
      if (row.some((value) => String(value).trim())) rows.push(row);
      row = [];
    } else if (char !== "\r") {
      cell += char;
    }
  }

  if (cell.length || row.length) {
    row.push(cell);
    if (row.some((value) => String(value).trim())) rows.push(row);
  }

  return rows.map((entry) => entry.map((value) => String(value).trim()));
}

function splitLearnings(value) {
  if (!value) return [];
  return value
    .split(/\s*·\s*|\s*\|\s*|\s*;\s*/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function extractYoutubeId(url) {
  if (!url) return "";
  const match = String(url).match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/|live\/))([a-zA-Z0-9_-]{11})/
  );
  return match?.[1] || "";
}

function normalizeTitle(title) {
  return String(title || "")
    .toLowerCase()
    .replace(/&amp;/g, "and")
    .split("|")[0]
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function findChannelVideo(sessionTitle, videos) {
  const needle = normalizeTitle(sessionTitle);
  if (!needle || !videos.length) return null;

  let best = null;
  let bestScore = 0;
  videos.forEach((video) => {
    const hay = normalizeTitle(video.title);
    if (!hay || !video.id) return;
    if (hay === needle) {
      best = video;
      bestScore = 999;
      return;
    }
    if (hay.startsWith(needle) || needle.startsWith(hay)) {
      const score = Math.min(hay.length, needle.length);
      if (score > bestScore) {
        best = video;
        bestScore = score;
      }
    }
  });
  return bestScore >= 18 ? best : null;
}

function thumbnailFor(videoId) {
  return videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : "";
}

async function loadChannelVideos() {
  try {
    const rssUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${PYCRAY_FORGE_CHANNEL_ID}`;
    const response = await fetch(
      `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`,
      { cache: "no-store" }
    );
    if (!response.ok) throw new Error("rss failed");
    const data = await response.json();
    const items = Array.isArray(data?.items) ? data.items : [];
    const videos = items
      .map((item) => ({
        title: item.title || "",
        id: extractYoutubeId(item.link || item.guid || ""),
      }))
      .filter((item) => item.id);
    if (videos.length) return videos;
  } catch {
    // Fall through to the known catalog.
  }
  return PYCRAY_FORGE_VIDEO_FALLBACK;
}

export async function attachYoutubeMedia(sessions) {
  const catalog = await loadChannelVideos();
  return sessions.map((session) => {
    const matched = findChannelVideo(session.title, catalog);
    const videoId = matched?.id || extractYoutubeId(session.recordingLink);
    return {
      ...session,
      videoId,
      thumbnail: thumbnailFor(videoId),
      recordingLink: videoId
        ? `https://www.youtube.com/watch?v=${videoId}`
        : session.recordingLink,
    };
  });
}

function cell(row, index) {
  return (row?.[index] || "").trim();
}

function findHeaderIndex(headers, matchers) {
  return headers.findIndex((header) =>
    matchers.some((matcher) => header.includes(matcher))
  );
}

export function parsePastSessions(csv) {
  const rows = parseCsv(csv);
  if (rows.length < 2) return [];

  const headers = rows[0].map((header) => header.toLowerCase().replace("#", "number"));
  const numberIdx = Math.max(0, findHeaderIndex(headers, ["number", "#"]));
  const titleIdx = Math.max(
    1,
    findHeaderIndex(headers, ["title"]) === -1 ? 1 : findHeaderIndex(headers, ["title"])
  );
  const learningsIdx =
    findHeaderIndex(headers, ["key learning", "learning"]) === -1
      ? 2
      : findHeaderIndex(headers, ["key learning", "learning"]);
  const linkIdx =
    findHeaderIndex(headers, ["recording", "youtube", "watch"]) === -1
      ? 3
      : findHeaderIndex(headers, ["recording", "youtube", "watch"]);

  return rows
    .slice(1)
    .map((row) => {
      const number = Number.parseInt(cell(row, numberIdx), 10);
      const title = cell(row, titleIdx);
      if (!title || Number.isNaN(number)) return null;
      return {
        number,
        title,
        learnings: splitLearnings(cell(row, learningsIdx)),
        recordingLink: cell(row, linkIdx),
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.number - a.number);
}

function mapFromKeyValue(rows) {
  const map = {};
  rows.forEach((row) => {
    let key = cell(row, 0);
    let value = cell(row, 1);
    if (!key) return;
    if (/^field$/i.test(key) && /^value$/i.test(value)) return;
    if (/^field/i.test(key) && /^value\s+/i.test(value)) {
      key = "Title";
      value = value.replace(/^value\s+/i, "");
    }
    key = key.replace(/^field\s+/i, "").trim();
    map[key.toLowerCase()] = value.replace(/^value\s+/i, "").trim();
  });
  return map;
}

export function parseUpcomingSession(csv) {
  const rows = parseCsv(csv);
  if (!rows.length) return null;

  const headers = rows[0].map((header) => header.toLowerCase());
  const hasTabularTitle = headers.some((header) => header === "title");
  const hasTabularDate = headers.some((header) => header.includes("date"));

  let data = {};
  if (hasTabularTitle && hasTabularDate && rows[1]) {
    const get = (matchers) => {
      const index = findHeaderIndex(headers, matchers);
      return index >= 0 ? cell(rows[1], index) : "";
    };
    data = {
      title: get(["title"]),
      date: get(["date"]),
      time: get(["time"]),
      speaker: get(["speaker"]),
      description: get(["description"]),
      registrationLink: get(["registration", "link", "form"]),
    };
  } else {
    const map = mapFromKeyValue(rows);
    data = {
      title: map.title || "",
      date: map.date || "",
      time: map.time || "",
      speaker: map.speaker || "",
      description: map.description || "",
      registrationLink: map["registration link"] || map.registration || map.link || "",
    };
  }

  if (!data.title) return null;
  return data;
}

async function fetchCsv(gid) {
  const response = await fetch(sessionsCsvUrl(gid), { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Failed to load sheet ${gid}`);
  }
  return response.text();
}

export async function loadSessionsFromSheet() {
  const [pastCsv, upcomingCsv] = await Promise.all([
    fetchCsv(SESSIONS_PAST_GID),
    fetchCsv(SESSIONS_UPCOMING_GID),
  ]);

  return {
    past: await attachYoutubeMedia(parsePastSessions(pastCsv)),
    upcoming: parseUpcomingSession(upcomingCsv),
  };
}
